export type StartRtcRoomEvent = {
  event: "start";
  user_id?: string;
  uid?: string;
  session_uid?: string;
  params: {
    model: string;
    size: [number, number];
    prompt: string;
    ref_image_path?: string;
    ref_image?: string;
    static_generate?: boolean;
    /** COS URL for `static_image_path` on this start; omit when unused. */
    static_image_path?: string;
    /** Whether the backend should horizontally flip the camera input. */
    mirror: boolean;
  } & Record<string, unknown>;
};

export type ChangeConditionRtcRoomEvent = {
  event: "change_condition";
  user_id?: string;
  session_uid?: string;
  params: {
    model: string;
    size: [number, number];
    prompt: string;
    ref_image_path?: string;
    ref_image?: string;
    static_generate?: boolean;
    /** COS URL for `static_image_path` on this start; omit when unused. */
    static_image_path?: string;
  } & Record<string, unknown>;
};

export type StopRtcRoomEvent = {
  event: "stop";
  user_id?: string;
  session_uid?: string;
};

export type TracksRtcRoomEvent = {
  event: "tracks";
  user_id?: string;
  uid?: string;
  session_uid?: string;
  tracks: Array<[number, number]>;
};

export type RtcRoomEvent =
  | StartRtcRoomEvent
  | ChangeConditionRtcRoomEvent
  | StopRtcRoomEvent
  | TracksRtcRoomEvent;

/** Generation task id sent as `uid` / `session_uid` in RTC room events. */
export function createTaskUid(): string {
  const now = Date.now();
  if (now !== lastTaskUidTimestamp) {
    lastTaskUidTimestamp = now;
    sameTickSequence = 0;
  } else {
    sameTickSequence += 1;
  }
  const randomPart = createRandomUint32();
  return `task-${now}-${sameTickSequence}-${randomPart}`;
}

let lastTaskUidTimestamp = 0;
let sameTickSequence = 0;

function createRandomUint32(): number {
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0]!;
  }
  return Math.floor(Math.random() * 0x1_0000_0000);
}

export function createStartRtcRoomEvent(input: {
  userId?: string;
  uid?: string;
  sessionUid?: string;
  model: string;
  size: [number, number];
  prompt?: string;
  refImagePath?: string;
  refImage?: string;
  /** When `true`, sent as `static_generate` on this start only. */
  staticGenerate?: boolean;
  /** COS URL sent as `static_image_path` on this start only. */
  staticImagePath?: string;
  /** Whether the backend should horizontally flip the camera input. */
  mirror: boolean;
  extraParams?: Record<string, unknown>;
}): StartRtcRoomEvent {
  const resolvedPrompt = input.prompt?.trim() ?? "";
  const refImagePath = input.refImagePath?.trim() || input.refImage?.trim() || undefined;
  const useRefImage = !!refImagePath;
  const staticImagePath = input.staticImagePath?.trim() || undefined;
  return {
    event: "start",
    user_id: input.userId?.trim() || undefined,
    uid: input.uid?.trim() || undefined,
    session_uid: input.sessionUid?.trim() || undefined,
    params: {
      model: input.model.trim(),
      size: input.size,
      prompt: resolvedPrompt,
      ref_image_path: useRefImage ? refImagePath : undefined,
      ref_image: useRefImage ? refImagePath : undefined,
      // Omit when false/undefined so starts never carry leftover `static_generate`.
      static_generate: input.staticGenerate === true ? true : undefined,
      static_image_path: staticImagePath,
      ...(input.extraParams ?? {}),
      mirror: input.mirror,
    },
  };
}

export function createChangeConditionRtcRoomEvent(input: {
  userId?: string;
  sessionUid?: string;
  model: string;
  size: [number, number];
  prompt?: string;
  refImagePath?: string;
  refImage?: string;
  /** When `true`, sent as `static_generate` on this change only. */
  staticGenerate?: boolean;
  /** COS URL sent as `static_image_path` on this change only. */
  staticImagePath?: string;
  extraParams?: Record<string, unknown>;
}): ChangeConditionRtcRoomEvent {
  const resolvedPrompt = input.prompt?.trim() ?? "";
  const refImagePath = input.refImagePath?.trim() || input.refImage?.trim() || undefined;
  const useRefImage = !!refImagePath;
  const staticImagePath = input.staticImagePath?.trim() || undefined;
  return {
    event: "change_condition",
    user_id: input.userId?.trim() || undefined,
    session_uid: input.sessionUid?.trim() || undefined,
    params: {
      model: input.model.trim(),
      size: input.size,
      prompt: resolvedPrompt,
      ref_image_path: useRefImage ? refImagePath : undefined,
      ref_image: useRefImage ? refImagePath : undefined,
      static_generate: input.staticGenerate === true ? true : undefined,
      static_image_path: staticImagePath,
      ...(input.extraParams ?? {}),
    },
  };
}

export function createStopRtcRoomEvent(input: { userId?: string; sessionUid?: string }): StopRtcRoomEvent {
  return {
    event: "stop",
    user_id: input.userId?.trim() || undefined,
    session_uid: input.sessionUid?.trim() || undefined,
  };
}

export function createTracksRtcRoomEvent(input: {
  userId?: string;
  uid?: string;
  sessionUid?: string;
  tracks: Array<[number, number]>;
}): TracksRtcRoomEvent {
  return {
    event: "tracks",
    user_id: input.userId?.trim() || undefined,
    uid: input.uid?.trim() || undefined,
    session_uid: input.sessionUid?.trim() || undefined,
    tracks: input.tracks,
  };
}
